/**
 * PROJECT BOARD (PROJECT-MAP.md §13) — the whole project as a Jira-style board of vertical LANES
 * (milestone × DAG wave), with the ARTIFACT edges drawn between cards (the coordination model IS
 * files). Exactly 4 lanes are on screen; ‹Prev/Next› page by 2; milestone pills (M0…Mn) between
 * them highlight what's loaded and jump on click. Default window = the ONGOING milestone. Cards
 * are big and spread; DAG alignment holds (producers in earlier lanes than consumers). A require
 * nothing in the project provides = a red badge on the card. Every milestone closes with an EXIT
 * lane carrying its goal card. Click a card → onSelectTask (the panel flips to Text at that
 * milestone). Lane/window math lives in the PURE builder (projectMapGraph.js) — vitest-pinned.
 */
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactFlow, { Background, Handle, Position } from 'reactflow';
import 'reactflow/dist/style.css';
import { clampStart, edgesForWindow, VISIBLE_LANES } from './projectMapGraph.js';
import MarkdownRenderer from '@stepflow/ui-shared/components/MarkdownRenderer.jsx';

const LANE_W = 330;          // card 290 + gutter
const CARD_W = 290;
const CARD_H = 104;
const ROW_GAP = 36;
const TOP_PAD = 64;          // room for the lane header

const STATUS_BG = {
  done: 'border-emerald-300 bg-emerald-50',
  ready: 'border-blue-300 bg-blue-50',
  blocked: 'border-amber-300 bg-amber-50',
  planned: 'border-slate-200 bg-white',
  approved: 'border-emerald-200 bg-emerald-50/60',
};
const STATUS_DOT = {
  done: 'bg-emerald-500', ready: 'bg-blue-500', blocked: 'bg-amber-500',
  planned: 'bg-slate-300', approved: 'bg-emerald-400',
};

const TaskCard = memo(({ data }) => (
  <div
    className={`rounded-lg border px-3 py-2 shadow-sm transition-opacity ${
      STATUS_BG[data.status] || STATUS_BG.planned} ${
      data.focused ? 'ring-2 ring-blue-500' : ''} ${
      data.dimmed ? 'opacity-30' : ''}`}
    style={{ width: CARD_W, height: CARD_H }}
    data-testid="pmg-task"
    title={`${data.taskId} · ${data.status}${data.owner ? ` · ${data.owner}` : ''}${
      data.unmet?.length ? `\nUNMET: ${data.unmet.join(', ')}` : ''}`}
  >
    <Handle type="target" position={Position.Left} className="!h-2 !w-2 !bg-slate-300" />
    <div className="flex items-center gap-1.5">
      <span className={`h-2 w-2 shrink-0 rounded-full ${STATUS_DOT[data.status] || STATUS_DOT.planned}`} />
      <span className="truncate text-xs font-semibold text-slate-700">{data.taskId}</span>
      {data.rev ? <span className="shrink-0 text-[10px] text-slate-400">r{data.rev}</span> : null}
      {data.unmet?.length ? (
        <span className="shrink-0 rounded bg-red-100 px-1 text-[9px] font-semibold text-red-600"
              data-testid="pmg-unmet">⚠ {data.unmet.length}</span>
      ) : null}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); data.onPlan?.(data.taskId, data.title); }}
        className="ml-auto shrink-0 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[9px] font-medium text-slate-600 shadow-sm hover:bg-slate-100"
        data-testid="pmg-plan-btn"
        title="View this task's plan file (goal · DoD · hand-offs)"
      >
        Plan
      </button>
    </div>
    <div className="mt-0.5 text-[11px] leading-snug text-slate-600"
         style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
      {data.title}
    </div>
    <div className="mt-1 truncate text-[10px] text-slate-400">{data.owner}</div>
    <Handle type="source" position={Position.Right} className="!h-2 !w-2 !bg-slate-300" />
  </div>
));

const LaneHeader = memo(({ data }) => (
  <div className="select-none" style={{ width: CARD_W }} data-testid="pmg-lane">
    <div className="text-[11px] font-semibold text-slate-500">
      {data.kind === 'exit' ? `🏁 ${data.milestoneId} exit` : `${data.milestoneId} · stage ${data.wave}`}
      {data.milestoneStatus === 'approved' ? <span className="ml-1 text-emerald-500">✓</span> : null}
    </div>
    <div className="truncate text-[10px] text-slate-400">{data.milestoneName}</div>
  </div>
));

const ExitCard = memo(({ data }) => (
  <div className="rounded-lg border-2 border-indigo-300 bg-indigo-50 px-3 py-2 shadow"
       style={{ width: CARD_W, minHeight: CARD_H }} data-testid="pmg-goal" title={data.exit}>
    <Handle type="target" position={Position.Left} className="!h-2 !w-2 !bg-indigo-400" />
    <div className="text-xs font-semibold text-indigo-700">🏁 {data.milestoneId} {data.milestoneName} — exit</div>
    <div className="mt-1 text-[11px] leading-snug text-indigo-600"
         style={{ display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
      {data.exit || 'Exit criterion not set for this milestone.'}
    </div>
  </div>
));

const NODE_TYPES = { task: TaskCard, laneHeader: LaneHeader, exit: ExitCard };

const EDGE_STYLE = {
  artifact: { stroke: '#94a3b8', strokeWidth: 1.5 },
  terminal: { stroke: '#818cf8', strokeWidth: 1.5 },
};

// Total drawable width of the 4-lane window (last lane has no trailing gutter).
const CONTENT_W = (VISIBLE_LANES - 1) * LANE_W + CARD_W + 32;

// CANVAS-ONLY: the panel owns the board + window state and renders the ‹Prev · pills · Next› nav
// in its compact header row (space-saving). This component just draws the visible 4 lanes.
export default function ProjectMapGraph({ board, start, onLoadTaskMd, onOpenPlanDoc = null }) {
  // PLAN POPOVER (§task-md, deliberate — user ask 2026-09-20): each card carries a "Plan" button
  // (top-right, beside the task id) that toggles a WIDE popover rendering the task's plan .md
  // (goal · DoD · flow · hand-off table — the exact file agents read). Click Plan again / another
  // card's Plan / the pane / ✕ to close. Fetched once per task (cache); scrollable.
  const [hover, setHover] = useState(null);          // {taskId, left, top, md|null, err}
  // LINEAGE HIGHLIGHT (user ask 2026-09-20): clicking a card no longer jumps to the Text view —
  // it highlights the block + its whole dependency chain (transitive producers upstream AND
  // consumers downstream, within the visible window); everything else dims. Click again / the
  // pane to clear.
  const [focusId, setFocusId] = useState(null);
  const mdCache = useRef(new Map());
  const nodePosRef = useRef(new Map());              // taskId → {x, y} for popover placement
  const openPlanRef = useRef(() => {});              // filled below; threaded into card data

  // WIDTH LOCK: zoom = container / content, so the 4 lanes always FILL the width edge to edge.
  const wrapRef = useRef(null);
  const rfRef = useRef(null);
  const zoomRef = useRef(1);
  const contentHRef = useRef(420);
  const [boardH, setBoardH] = useState(420);
  const lockWidth = useCallback((instance) => {
    rfRef.current = instance || rfRef.current;
    const inst = rfRef.current;
    const w = wrapRef.current?.clientWidth || 0;
    if (!inst || !w) return;
    const zoom = Math.min(2, Math.max(0.2, w / CONTENT_W));
    zoomRef.current = zoom;
    inst.setViewport({ x: 0, y: 8, zoom });
    // STATIC BOARD (user ask): the wrapper grows to the tallest visible lane so NOTHING inside
    // ever needs panning — the page scrolls, the canvas is frozen; buttons are the only nav.
    setBoardH(Math.max(360, Math.ceil(contentHRef.current * zoom) + 16));
  }, []);
  useEffect(() => {                     // keep the lock on window resizes
    const onResize = () => lockWidth();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [lockWidth]);
  // FIRST-PAINT MEASURE RACE (user report 2026-09-20): on the very first render the wrapper's
  // clientWidth can still be 0 (tab/panel laying out) → lockWidth bails (`!w`) → boardH stays at
  // the 420 default and the board doesn't spread vertically. No window 'resize' fires when the
  // container goes 0→real width, so nothing re-locked until a nav-button remount (key={s}) forced
  // a re-measure — which is why pressing M1/M2 and coming back "fixed" it. A ResizeObserver on the
  // wrapper re-locks the moment it gets a real width (and on any later container resize).
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => lockWidth());
    ro.observe(el);
    return () => ro.disconnect();
  }, [lockWidth]);

  const { nodes, edges, contentH } = useMemo(() => {
    const s = clampStart(start, board.lanes.length);
    const visible = board.lanes.slice(s, s + VISIBLE_LANES);
    const ns = [];
    visible.forEach((lane, i) => {
      const x = i * LANE_W + 16;
      ns.push({ id: `lane:${lane.id}`, type: 'laneHeader', position: { x, y: 0 },
                draggable: false, selectable: false,
                data: { kind: lane.kind, milestoneId: lane.milestoneId,
                        milestoneName: lane.milestoneName, wave: lane.wave,
                        milestoneStatus: lane.milestoneStatus } });
      if (lane.kind === 'exit') {
        ns.push({ id: `exit:${lane.milestoneId}`, type: 'exit', position: { x, y: TOP_PAD },
                  data: { milestoneId: lane.milestoneId, milestoneName: lane.milestoneName,
                          exit: lane.exit } });
        return;
      }
      lane.items.forEach((c, j) => {
        const pos = { x, y: TOP_PAD + j * (CARD_H + ROW_GAP) };
        nodePosRef.current.set(c.taskId, pos);
        ns.push({ id: c.taskId, type: 'task', position: pos,
                  data: { ...c, onPlan: openPlanRef.current } });
      });
    });
    // CLEAN LINES: no filename labels on edges — the artifact contract belongs to the TASK and
    // renders in the Plan popover. ``label`` stays in the pure edge data; we just don't draw it.
    const rawEdges = edgesForWindow(board.lanes, s);

    // LINEAGE: from the focused card, walk the visible edges transitively UP (producers) and
    // DOWN (consumers); the chain stays vivid, the rest dims.
    let chain = null;
    if (focusId && ns.some((n) => n.id === focusId)) {
      const up = new Map(); const down = new Map();
      for (const e of rawEdges) {
        (down.get(e.source) || down.set(e.source, []).get(e.source)).push(e.target);
        (up.get(e.target) || up.set(e.target, []).get(e.target)).push(e.source);
      }
      chain = new Set([focusId]);
      const walk = (id, adj) => {
        for (const nxt of (adj.get(id) || [])) {
          if (!chain.has(nxt)) { chain.add(nxt); walk(nxt, adj); }
        }
      };
      walk(focusId, up);
      walk(focusId, down);
    }
    const withDim = chain
      ? ns.map((n) => (n.type === 'laneHeader' ? n
          : { ...n, data: { ...n.data, focused: n.id === focusId, dimmed: !chain.has(n.id) } }))
      : ns;
    const es = rawEdges.map(({ label, ...e }) => {
      const inChain = chain && chain.has(e.source) && chain.has(e.target);
      return {
        ...e,
        type: 'smoothstep',
        style: chain
          ? (inChain ? { stroke: '#3b82f6', strokeWidth: 2.25 }
                     : { ...(EDGE_STYLE[e.data?.kind] || EDGE_STYLE.artifact), opacity: 0.15 })
          : (EDGE_STYLE[e.data?.kind] || EDGE_STYLE.artifact),
      };
    });
    const maxY = ns.reduce((m, n) => Math.max(m, n.position.y), 0);
    return { nodes: withDim, edges: es, contentH: maxY + CARD_H + 24 };
  }, [board, start, focusId]);

  contentHRef.current = contentH;
  useEffect(() => { lockWidth(); }, [contentH, lockWidth]);

  const POP_W = 840;   // wide read — the hand-off table needs the room
  const openPlan = (taskId, title) => {
    // DOCK-FIRST (2026-09-23): the plan .md opens as an Agent Stream Dock tab (consistent with agent
    // streams), replacing the bespoke popover. The popover below is kept only as a fallback for a
    // standalone mount with no dock callback.
    if (onOpenPlanDoc) { onOpenPlanDoc(taskId, title); return; }
    if (hover?.taskId === taskId) { setHover(null); return; }      // toggle off on second click
    const pos = nodePosRef.current.get(taskId);
    if (!pos) return;
    const zoom = zoomRef.current || 1;
    const wrapW = wrapRef.current?.clientWidth || 1200;
    const rightX = (pos.x + CARD_W + 14) * zoom;
    const left = rightX + POP_W > wrapW ? Math.max(4, pos.x * zoom - POP_W - 14) : rightX;
    const top = Math.max(4, pos.y * zoom);
    const cached = mdCache.current.get(taskId);
    setHover({ taskId, left, top, md: cached ?? null, err: '' });
    if (cached === undefined && onLoadTaskMd) {
      Promise.resolve(onLoadTaskMd(taskId))
        .then((res) => {
          const md = (res && (res.markdown || res.md)) || '';
          mdCache.current.set(taskId, md);
          setHover((h) => (h && h.taskId === taskId ? { ...h, md } : h));
        })
        .catch(() => setHover((h) => (h && h.taskId === taskId
          ? { ...h, md: '', err: 'could not load the task file' } : h)));
    }
  };

  openPlanRef.current = openPlan;

  if (!board?.lanes?.length) return null;

  const s = clampStart(start, board.lanes.length);

  return (
    <div data-testid="pm-graph">
      <div
        ref={wrapRef}
        className="relative rounded-md border border-slate-200 bg-slate-50/50"
        // STATIC BOARD: height = the tallest visible lane (scaled) — the canvas never pans or
        // zooms; the PAGE scrolls and the Prev/Next/pill buttons are the ONLY navigation.
        style={{ height: boardH, minHeight: 360 }}
      >
        <ReactFlow
          key={s}                       /* remount per page → viewport re-locks on Prev/Next/pill */
          nodes={nodes}
          edges={edges}
          nodeTypes={NODE_TYPES}
          // JIRA-BOARD VIEWPORT (no zoom-to-fit): fitView shrank the whole board to fit the
          // TALLEST lane, wasting the width. Instead LOCK zoom so the 4 lanes exactly FILL the
          // container width; long lanes scroll VERTICALLY (wheel/trackpad pans, no zooming).
          onInit={lockWidth}
          minZoom={0.2}
          maxZoom={2}
          zoomOnScroll={false}
          zoomOnPinch={false}
          zoomOnDoubleClick={false}
          panOnScroll={false}
          panOnDrag={false}
          preventScrolling={false}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable
          proOptions={{ hideAttribution: true }}
          onNodeClick={(_, node) => {
            if (node?.type === 'task') {
              setFocusId((prev) => (prev === node.id ? null : node.id));
            }
          }}
          onPaneClick={() => { setHover(null); setFocusId(null); }}
        >
          <Background gap={20} size={1} color="#e2e8f0" />
        </ReactFlow>
        {hover ? (
          <div
            className="absolute z-20 max-h-[65vh] overflow-y-auto rounded-lg border border-slate-300 bg-white p-3 shadow-xl"
            style={{ left: hover.left, top: hover.top, width: POP_W }}
            data-testid="pmg-hover-md"
          >
            <button
              type="button"
              onClick={() => setHover(null)}
              className="absolute right-1.5 top-1.5 rounded px-1.5 py-0.5 text-[11px] text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              data-testid="pmg-plan-close"
              aria-label="Close plan"
            >
              ✕
            </button>
            {hover.md === null && !hover.err ? (
              <div className="text-[11px] text-slate-400">loading {hover.taskId}…</div>
            ) : hover.err ? (
              <div className="text-[11px] text-red-500">{hover.err}</div>
            ) : hover.md ? (
              <div className="prose prose-xs max-w-none text-[11px]">
                <MarkdownRenderer content={hover.md} />
              </div>
            ) : (
              <div className="text-[11px] text-slate-400">no task file for {hover.taskId}</div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
